/**
 * ONE BAR FOR A LONG WAIT ON THE NEW PLAYER'S PATH.
 *
 * "Continue with Google", "Create Account" and "Enter Moonfall" each wait on
 * the network for as long as it takes (1.6-5.8 s measured on round 15 of the
 * snappiness meter), and the pressed button's pending look was all the player
 * saw. The press is still answered at once by that look; a wait that outlasts
 * the shared grace (docs/asset-loading.md, 450 ms) also gets ONE bar — a rail
 * inside the pressed button, so nothing around it moves — marked
 * `data-loading` for what it is. A wait that ends inside the grace never shows
 * it, and there is never more than one: arming a bar ends any other.
 *
 * THE GRACE STARTS AT THE PRESS. A tap's click arrives about 100 ms after its
 * pointerdown, so a bar timed from the click showed past the grace; the last
 * press on the control, if it is recent, is where the wait began.
 */

const AUTH_WAIT_BAR_GRACE_MS = 450;
/* A press older than this is not a tap that started this wait but a button
   held down (or a cancelled press): the wait then starts at the click, so a
   long hold never makes the bar flash in the instant it is released. */
const PRESS_FRESH_MS = 250;

/** @type {{ control: Element, timer: ReturnType<typeof setTimeout> | null, bar: HTMLElement | null } | null} */
let armed = null;

/** @type {{ target: EventTarget | null, at: number } | null} */
let lastPress = null;
if (typeof document !== 'undefined') {
    document.addEventListener('pointerdown', (event) => {
        lastPress = { target: event.target, at: Date.now() };
    }, { capture: true, passive: true });
}

/** How much of the grace is left for a wait on `control` that starts now. */
function graceLeft(control) {
    const press = lastPress;
    if (!press || !(press.target instanceof Node) || !control.contains(press.target)) return AUTH_WAIT_BAR_GRACE_MS;
    const since = Date.now() - press.at;
    if (since < 0 || since > PRESS_FRESH_MS) return AUTH_WAIT_BAR_GRACE_MS;
    return Math.max(0, AUTH_WAIT_BAR_GRACE_MS - since);
}

/** The wait is over, whichever it was: the bar (or its pending timer) goes. */
function disarmAll() {
    if (!armed) return;
    const { timer, bar } = armed;
    armed = null;
    if (timer) clearTimeout(timer);
    if (bar && bar.parentNode) bar.parentNode.removeChild(bar);
}

/**
 * This control's wait is over. A bar another control has since taken is left
 * alone.
 * @param {Element | null | undefined} control
 */
function disarm(control) {
    if (armed && control && armed.control === control) disarmAll();
}

/**
 * This control's wait is over, but its surface is still leaving (a card
 * fading out over the screen that replaced it): a bar not shown yet never
 * will be, and one on screen leaves WITH the control rather than a frame of
 * nothing before it.
 * @param {Element | null | undefined} control
 */
function settle(control) {
    if (!armed || !control || armed.control !== control) return;
    if (armed.timer) clearTimeout(armed.timer);
    armed = null;
}

/**
 * Put the bar inside `control` once the grace has passed, unless the wait has
 * ended by then (`disarm`) or the control has left the page.
 * @param {Element | null | undefined} control
 */
function arm(control) {
    disarmAll();
    if (!control) return;
    const entry = { control, timer: null, bar: null };
    entry.timer = setTimeout(() => {
        entry.timer = null;
        if (armed !== entry || !control.isConnected) return;
        const bar = document.createElement('span');
        bar.className = 'auth-loading-bar';
        bar.setAttribute('data-loading', 'true');
        bar.setAttribute('aria-hidden', 'true');
        control.appendChild(bar);
        entry.bar = bar;
    }, graceLeft(control));
    armed = entry;
}

const AuthWaitBar = { arm, disarm, disarmAll, settle, GRACE_MS: AUTH_WAIT_BAR_GRACE_MS };

export { AuthWaitBar };
